import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  LoadingSpinner,
  ScrollArea,
} from '@ais-ui/ui-core';
import React, { useMemo } from 'react';
import { usePortGet, usePortsGetList } from '@ais-ui/api';
import { cn } from '@ais-ui/utils';
import { PiAnchorThin, PiBoatThin } from 'react-icons/pi';
import { Port, useAISMapContext } from '../AISMapContext';
import { ShipSearchResult } from './useShipSearch';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { PortList } from '@ais-ui/api-spec/portserver';
import { fromLonLat } from 'ol/proj';
import { Point } from 'ol/geom';
import { useSelectionState } from '../../../components/SelectionStateProvider';

interface PortSearchProps {
  searchString: string;
  setSearchString: React.Dispatch<React.SetStateAction<string>>;
}

export const PortSearch = ({
  searchString,
  setSearchString,
}: PortSearchProps) => {
  const { map } = useOpenLayersContext();
  const { data, isLoading } = usePortsGetList();
  const { setSelectedPorts } = useSelectionState();

  const filteredPorts = useMemo(() => {
    const filtered =
      data?.records.filter(
        (port) =>
          port.name?.toLowerCase().includes(searchString.toLowerCase()) ||
          port.locode?.toLowerCase().includes(searchString.toLowerCase()) ||
          port.country?.toLowerCase().includes(searchString.toLowerCase())
      ) ?? [];

    // Return only the first 50 matching results
    return filtered.slice(0, 150);
  }, [data, searchString]);

  const scrollAreaHeightClass = useMemo(() => {
    const itemHeight = 52; // Height for each item in pixels
    const maxHeight = 256; // Maximum allowed height in pixels

    if (!filteredPorts || filteredPorts.length === 0) return '';

    const totalHeight = Math.min(filteredPorts.length * itemHeight, maxHeight);

    return `h-[${totalHeight}px]`;
  }, [filteredPorts]);

  const onClick = (port: PortList) => {
    setSearchString(port.name);

    const selectedPort: Port = {
      id: port.id.toString(),
      name: port.name,
      country: port.country,
      locode: port.locode,
      geometry: new Point(
        fromLonLat(port.position.coordinates as [number, number])
      ),
    };
    setSelectedPorts([selectedPort]);

    const [longitude, latitude] = port.position.coordinates as [number, number];
    const view = map.getView();

    // Transform coordinates from [lon, lat] to map projection
    const transformedPosition = fromLonLat([longitude, latitude]);

    // Set the map's center and zoom level
    view.setCenter(transformedPosition);
    view.setZoom(12);
  };

  return (
    <Command className="h-fit">
      <CommandList className="h-fit ">
        <CommandGroup
          heading={
            searchString.length >= 3
              ? `ports (${filteredPorts.length})`
              : 'ports'
          }
        ></CommandGroup>
        {!isLoading && searchString.length < 3 && (
          <div className="py-6 text-center text-sm">
            Enter three or more characters to search.
          </div>
        )}
        {!isLoading &&
          searchString.length >= 3 &&
          filteredPorts.length === 0 && (
            <div className="py-6 text-center text-sm">No ports found.</div>
          )}
        {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
        {searchString.length >= 3 && filteredPorts.length > 0 && (
          <ScrollArea className={cn('h-[256px]', scrollAreaHeightClass)}>
            {filteredPorts.map((port, index) => (
              <CommandItem
                className="mr-4 hover:cursor-pointer"
                onSelect={() => onClick(port)}
                key={index}
              >
                <div className="flex flex-col space-y-1 w-full">
                  <div className="flex justify-between">
                    <div className="flex items-center space-x-1">
                      <PiAnchorThin className={cn('mr-2 h-4 w-4')} />
                      {port.name}
                    </div>
                  </div>
                  <div className="flex space-x-2 text-xs text-muted-foreground items-center">
                    <div>Locode: {port.locode}</div>
                    <div
                      className={cn('rounded-full h-1 w-1 bg-blue-600')}
                    ></div>
                    <div>Country: {port.country}</div>
                  </div>
                </div>
              </CommandItem>
            ))}
          </ScrollArea>
        )}
      </CommandList>
    </Command>
  );
};
